import { Box, TextField, Select, MenuItem, InputAdornment, IconButton, FormControl, Typography, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import themeColors from "../theme";

const priceTiers = [
    { label: "Under $50", value: "under50" },
    { label: "$50 - $200", value: "50-200" },
    { label: "$200 - $500", value: "200-500" },
    { label: "$500+", value: "500plus" },
];

const allCategories = [
    "All",
    "Books",
    "Conferences",
    "Digital Apps",
    "Medical Equipment",
    "Medical Journals",
    "Online Education",
    "Podcast",
];

const sortOptions = [
    { value: "none", label: "Default" },
    { value: "price-asc", label: "Price: Low to High" },
    { value: "price-desc", label: "Price: High to Low" },
    { value: "title-asc", label: "Title: A-Z" },
    { value: "title-desc", label: "Title: Z-A" },
];

export default function MarketplaceTopBar({
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    selectedPriceTiers = [],
    onTogglePriceTier,
    sortBy,
    setSortBy,
    onClear,
    showPriceFilters = true,
}) {
    const { teal } = themeColors;
    const visibleSortOptions = showPriceFilters
        ? sortOptions
        : sortOptions.filter((opt) => !opt.value.startsWith("price-"));

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1.5,
                backgroundColor: "#fff",
                borderRadius: 2,
                flexWrap: "wrap",
                border: "1px solid #e9ecef",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
        >
            {/* Search */}
            <TextField
                size="small"
                placeholder="Search CME products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    minWidth: 200,
                    flex: 1,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        "& fieldset": { borderColor: "#dee2e6" },
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#999" }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Category Dropdown */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& fieldset": { borderColor: "#dee2e6" },
                        fontWeight: selectedCategory !== "All" ? 600 : 400,
                    }}
                    renderValue={(val) => (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "#666", mr: 0.5 }}>Category</Typography>
                            <Typography variant="body2" fontWeight={600}>{val}</Typography>
                        </Box>
                    )}
                >
                    {allCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                            {cat}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Price Tier Chips */}
            {showPriceFilters && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ color: "#666", mr: 0.25 }}>Price</Typography>
                    {priceTiers.map((tier) => {
                        const isSelected = selectedPriceTiers.includes(tier.value);
                        return (
                            <Chip
                                key={tier.value}
                                label={tier.label}
                                size="small"
                                variant={isSelected ? "filled" : "outlined"}
                                onClick={() => onTogglePriceTier(tier.value)}
                                sx={{
                                    fontWeight: isSelected ? 600 : 400,
                                    backgroundColor: isSelected ? teal : "transparent",
                                    color: isSelected ? "#fff" : "#555",
                                    borderColor: isSelected ? teal : "#dee2e6",
                                    "&:hover": {
                                        backgroundColor: isSelected ? teal : "#f0f0f0",
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Sort */}
            <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& fieldset": { borderColor: "#dee2e6" },
                    }}
                    renderValue={(val) => (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "#666", mr: 0.5 }}>Sort</Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {visibleSortOptions.find((o) => o.value === val)?.label || "Recommended"}
                            </Typography>
                        </Box>
                    )}
                >
                    {visibleSortOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Clear */}
            <IconButton
                onClick={onClear}
                sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #dee2e6",
                    borderRadius: "50%",
                    width: 36,
                    height: 36,
                    "&:hover": { backgroundColor: "#f8f9fa" },
                }}
            >
                <CloseIcon fontSize="small" sx={{ color: "#666" }} />
            </IconButton>
        </Box>
    );
}
